// src/services/receptionist/devMock.ts
//
// DEV-ONLY fixtures for the Receptionist console API (ourteam-frontend#3606).
// The backend (ourteam-backend#4666) does not exist yet, so this simulates the
// whole contract in memory: reads, writes that round-trip within a session,
// and every EXPECTED non-2xx the screens have to handle — the 404 that means
// "not yours", the 409s on a booking decision, the 422 that carries the
// validator's own words. A page reload resets it.
//
// Only ./index.ts imports this, and only while VITE_ENABLE_RECEPTIONIST_LIVE_API
// is anything other than "true". Nothing else may import from here.
//
// The fixtures themselves still live where the screens read them today
// (@receptionist/merchant, bookings, transcripts, flow). This file wraps them
// rather than moving them: the screens keep importing demo*() until their own
// tickets switch them to the service, and moving the data first would break
// every screen at once for no gain. Structured-clone on every return, so a
// caller mutating the result cannot edit the fixture in place — which is also
// how a real endpoint behaves.

import { demoMerchant, defaultEscalationRules, LOCKED_FOR_CLINICS, type MerchantConfig } from "@receptionist/merchant";
import { demoBookings, isReplyWindowOpen, type Booking } from "@receptionist/bookings";
import { demoProposals, type TranscriptFile } from "@receptionist/transcripts";
import { defaultBookingFlow, clinicBookingFlow, validateFlow, type BookingFlow } from "@receptionist/flow";
import type {
    AnalyseResult,
    BookingFlowInput,
    ApplyProposalsResult,
    BookingDecisionResult,
    SendBookingMessageResult,
    BookingMessageInput,
    WhatsappTemplateKey,
    BookingsPage,
    FlowTemplate,
    ListBookingsParams,
    ListMerchantsParams,
    MerchantConfigInput,
    MerchantResult,
    MerchantSummary,
    MerchantsPage,
    MigrateResult,
    SaveFlowResult,
    SaveMerchantResult,
    ReceptionistOverview,
    ServiceImageResult,
    SendStaffTestResult,
    StaffChannelState,
    WhatsappState,
} from "./types";

const LATENCY_MS = 250;
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const wait = <T>(v: T): Promise<T> =>
    new Promise((resolve) => window.setTimeout(() => resolve(clone(v)), LATENCY_MS));

// ─── Stores ──────────────────────────────────────────────────────────────────

/**
 * The demo dataset: four merchants, one per category, at four different
 * points of onboarding, so every state the console can show is reachable
 * without special tooling —
 *
 *   ownlab-secret-skin  clinic  live, busy queue, escalations, all templates approved
 *   urban-sprin         salon   templates pending, a few bookings, casual tone
 *   serene-spa          spa     mid-migration, no bookings yet
 *   pearl-dental        dental  just provisioned, details only
 */
function variant(overrides: Partial<MerchantConfig> & Pick<MerchantConfig, "id" | "name" | "category">): MerchantConfig {
    const base = demoMerchant();
    return {
        ...base,
        ...overrides,
        escalationRules: defaultEscalationRules(overrides.category),
        closedDates: overrides.closedDates ?? [],
        faqNotes: overrides.faqNotes ?? base.faqNotes,
        people: overrides.people ?? base.people,
        services: overrides.services ?? base.services,
    };
}

const DEMO_MERCHANTS: MerchantConfig[] = [
    demoMerchant(),
    variant({
        id: "urban-sprin", name: "Urban Sprin", category: "salon", outlet: "Bangsar",
        address: "Jalan Telawi 3, Bangsar, Kuala Lumpur",
        whatsappNumber: "+60 17-XXX XXXX", whatsappConnected: true, approvedTemplates: 2,
        languages: ["Bahasa Melayu", "English"], tone: "salon_casual",
        services: [
            { id: "cut", name: "Cut and blow dry", durationMinutes: 45, price: 88, firstVisitPrice: 68, bookable: true },
            { id: "colour", name: "Full colour", durationMinutes: 120, price: 288, firstVisitPrice: 248, bookable: true },
            { id: "treatment", name: "Keratin treatment", durationMinutes: 150, price: 480, firstVisitPrice: 480, bookable: true },
            { id: "brows", name: "Eyebrow embroidery", durationMinutes: 90, price: 450, firstVisitPrice: 450, bookable: false },
        ],
        cancellationPolicy: "Let us know a day before and there is no charge.",
        depositPolicy: "RM50 deposit for colour and keratin, deducted from the bill.",
        faqNotes: [{ label: "Parking", body: "Street parking on Jalan Telawi; the bank next door has a paid lot." }],
        remind2h: false, unconfirmedChaseHours: 4,
        staffChannel: { name: "Sprin Team", connected: true, memberCount: 2 },
        people: [{ id: "p1", name: "Sprin", role: "Owner", phone: "017-XXX XXXX", consoleAccess: true }],
    }),
    variant({
        id: "serene-spa", name: "Serene Spa", category: "spa", outlet: "Mont Kiara",
        address: "1 Jalan Kiara, Mont Kiara, Kuala Lumpur",
        whatsappNumber: "+60 11-XXX XXXX", whatsappConnected: false, approvedTemplates: 0,
        languages: ["English", "中文"], tone: "clinic_formal",
        services: [
            { id: "swedish", name: "Swedish massage 60 min", durationMinutes: 60, price: 180, firstVisitPrice: 150, bookable: true },
            { id: "hotstone", name: "Hot stone massage 90 min", durationMinutes: 90, price: 260, firstVisitPrice: 260, bookable: true },
            { id: "couples", name: "Couples package", durationMinutes: 120, price: 520, firstVisitPrice: 520, bookable: false },
        ],
        cancellationPolicy: "Cancellations within 12 hours are charged 50%.",
        depositPolicy: "No deposit required.",
        faqNotes: [{ label: "Arrival", body: "Please arrive 10 minutes early to change; late arrivals shorten the session." }],
        afterHoursCapture: true, digestTime: "09:00",
        staffChannel: { name: "Serene Front Desk", connected: false, memberCount: 0 },
        people: [{ id: "p1", name: "Mei", role: "Manager", phone: "011-XXX XXXX", consoleAccess: true }],
    }),
    variant({
        id: "pearl-dental", name: "Pearl Dental", category: "dental", outlet: "Damansara Heights",
        address: "Lorong Setiabistari 2, Damansara Heights, Kuala Lumpur",
        whatsappNumber: "", whatsappConnected: false, approvedTemplates: 0,
        languages: ["English"], tone: "clinic_formal",
        services: [
            { id: "scaling", name: "Scaling and polishing", durationMinutes: 45, price: 150, firstVisitPrice: 120, bookable: true },
            { id: "whitening", name: "Teeth whitening", durationMinutes: 90, price: 890, firstVisitPrice: 890, bookable: true },
        ],
        cancellationPolicy: "Please give 24 hours' notice.",
        depositPolicy: "No deposit required.",
        faqNotes: [],
        hours: demoMerchant().hours.map((h) => (h.day === 6 ? { ...h, open: null, close: null } : h)),
        staffChannel: { name: "", connected: false, memberCount: 0 },
        people: [{ id: "p1", name: "Dr. Lim", role: "Dentist", phone: "012-XXX XXXX", consoleAccess: true }],
        alertMorningDigest: false,
    }),
];

const merchants = new Map<string, MerchantConfig>(DEMO_MERCHANTS.map((m) => [m.id, m]));
const flows = new Map<string, BookingFlow>();
/** A salon's queue: fewer rows, its own services, a reschedule and an escalation. */
function salonBookings(): Booking[] {
    return [
        {
            id: "u1", customer: "Farah", phone: "013-XXX XXXX", service: "Full colour", slot: "Sat 6 Sept, 11.00am",
            source: "Chat", status: "Pending", tags: ["Mixed BM and English", "Deposit quoted"],
            transcript: [
                { who: "customer", text: "hi, nak buat colour sabtu ni boleh?" },
                { who: "ai", text: "Boleh! Full colour ambil masa dalam 2 jam, RM288 (first visit RM248). Ada RM50 deposit, ditolak dari bil. Pukul berapa Sabtu ni?" },
                { who: "customer", text: "11am. Farah" },
                { who: "ai", text: "Noted, Farah. Full colour, Sabtu 6 Sept, 11.00 pagi. Team akan confirm sekejap lagi." },
            ],
        },
        {
            id: "u2", customer: "Daniel", phone: "016-XXX XXXX", service: "Cut and blow dry", slot: "Thu 4 Sept, 6.30pm",
            source: "Reschedule in chat", status: "Confirmed", note: "Moved from Wed 3 Sept, confirmed by Sprin", tags: ["Rescheduled"],
            transcript: [
                { who: "customer", text: "can i move my wednesday cut to thursday evening?" },
                { who: "ai", text: "Of course. You have a Cut and blow dry on Wed 3 Sept at 6.00pm. Which time on Thursday?" },
                { who: "customer", text: "6.30" },
                { who: "ai", text: "Cut and blow dry, Thu 4 Sept, 6.30pm. I have passed it to the team to confirm the new slot." },
                { who: "system", text: "Sprin confirmed in the staff group" },
                { who: "ai", text: "Done, Daniel: Thursday 4 Sept at 6.30pm is confirmed. See you then!" },
            ],
        },
        {
            id: "u3", customer: "Aishah", phone: "019-XXX XXXX", service: "Keratin treatment", slot: "Sun 7 Sept, 2.00pm",
            source: "Chat", status: "Pending", tags: ["Escalated first", "Price negotiation"],
            transcript: [
                { who: "customer", text: "keratin RM480 boleh kurang tak? kawan saya dapat RM400" },
                { who: "ai", text: "Untuk harga, saya minta owner kami balas terus ya. Saya dah maklumkan, dan mereka akan reply di sini." },
                { who: "system", text: "Escalated to Sprin: price negotiation" },
                { who: "ai", text: "Sprin here! RM480 tu dah termasuk aftercare kit. Kalau ambil sekali dengan cut, saya bagi RM520 untuk dua-dua." },
                { who: "customer", text: "ok ok, ahad 2pm boleh? Aishah" },
                { who: "system", text: "Handed back to the AI with #back" },
                { who: "ai", text: "Noted, Aishah. Keratin treatment, Ahad 7 Sept, 2.00 petang. Team akan confirm." },
            ],
        },
        {
            id: "u4", customer: "Kevin", phone: "012-XXX XXXX", service: "Cut and blow dry", slot: "Tue 2 Sept, 7.00pm",
            source: "After-hours chat", status: "Declined", note: "Declined, closed on Tuesday evening. Wednesday offered", tags: ["After hours"],
            transcript: [
                { who: "system", text: "Received 9.48pm, shop closed" },
                { who: "customer", text: "you open tonight? need a quick trim" },
                { who: "ai", text: "We are closed for the night and open tomorrow at 10am. I can take a request now and the team will confirm first thing. Which day suits?" },
                { who: "customer", text: "tuesday 7pm" },
                { who: "system", text: "Declined in the staff group, closed Tuesday evenings" },
                { who: "ai", text: "Sorry Kevin, we are closed on Tuesday evenings. Wednesday at 7pm or Thursday at 11am?" },
            ],
        },
    ];
}

const bookings = new Map<string, Booking[]>([
    ["ownlab-secret-skin", demoBookings()],
    ["urban-sprin", salonBookings()],
    // Serene Spa and Pearl Dental are not live yet: an empty queue is the
    // honest state, and it is the empty state the screen has to render.
    ["serene-spa", []],
    ["pearl-dental", []],
]);
const whatsapp = new Map<string, WhatsappState>();
const transcripts = new Map<string, TranscriptFile[]>();

/** The locks are the server's to compute from `category`, never stored. */
function withLocks(m: MerchantConfig): MerchantConfig {
    const clinical = m.category === "clinic" || m.category === "dental";
    return {
        ...m,
        escalationRules: m.escalationRules.map((r) => {
            if (r.key === "unknown") return { ...r, locked: true, lockNote: "The AI never guesses." };
            if (LOCKED_FOR_CLINICS.includes(r.key)) {
                return clinical
                    ? { ...r, locked: true, lockNote: "Always on for a clinic." }
                    : { ...r, locked: false, lockNote: undefined };
            }
            return r;
        }),
    };
}

function whatsappFor(merchantId: string): WhatsappState {
    const existing = whatsapp.get(merchantId);
    if (existing) return existing;
    const m = merchants.get(merchantId);
    const live = m?.whatsappConnected ?? false;
    // Four stages across the four merchants, so each checklist state is
    // reachable from the roll-up without editing anything.
    const stage: WhatsappState["onboardingStage"] =
        merchantId === "ownlab-secret-skin" ? "kb_signoff"
        : merchantId === "urban-sprin" ? "templates_pending"
        : merchantId === "serene-spa" ? "number_migration"
        : "details";
    const approvedCount = merchantId === "urban-sprin" ? 2 : live ? 7 : 0;
    // Shared-number mode (ourteam-backend#4666, 3 Sept 2026): every merchant
    // runs on the platform number for now, so the number-related checks read
    // done and the merchant shares an entry link instead of migrating.
    const state: WhatsappState = {
        number: "+60123456789",
        displayNumber: "+60 12-XXX XXXX",
        connected: true,
        checklist: {
            detailsConfirmed: Boolean(m?.name && m?.address),
            metaVerified: stage !== "details",
            numberMigrated: stage === "kb_signoff" || stage === "templates_pending",
            templatesApproved: stage === "kb_signoff",
            kbSignedOff: false,
        },
        onboardingStage: stage,
        oneWayDoorAcceptedAt: stage === "number_migration" || live ? "2026-08-28T03:11:00Z" : null,
        // Ownlab and Urban Sprin ride the platform number (the shared-number
        // decision); Serene is mid-way through moving its own; Pearl has not
        // started. Both shapes are on screen somewhere.
        onPlatformNumber: merchantId === "ownlab-secret-skin" || merchantId === "urban-sprin",
        entryLink: merchantId === "ownlab-secret-skin" || merchantId === "urban-sprin"
            ? `https://wa.me/60123456789?text=${encodeURIComponent(`[RCP:${merchantId}]`)}`
            : null,
        templates: [
            { key: "booking_confirmed", status: approvedCount >= 1 ? "approved" : "not_submitted", metaName: approvedCount >= 1 ? "ot_booking_confirmed_v1" : null, submittedAt: approvedCount >= 1 ? "2026-08-28T04:00:00Z" : null, reviewedAt: approvedCount >= 1 ? "2026-08-28T09:12:00Z" : null, rejectionReason: null },
            { key: "reminder_24h", status: approvedCount >= 2 ? "approved" : merchantId === "urban-sprin" ? "rejected" : "not_submitted", metaName: approvedCount >= 2 || merchantId === "urban-sprin" ? "ot_reminder_24h_v1" : null, submittedAt: approvedCount >= 2 || merchantId === "urban-sprin" ? "2026-08-28T04:00:00Z" : null, reviewedAt: approvedCount >= 2 ? "2026-08-28T09:12:00Z" : merchantId === "urban-sprin" ? "2026-09-01T10:30:00Z" : null, rejectionReason: merchantId === "urban-sprin" ? "Template body contains a promotional phrase; utility templates must not advertise." : null },
            { key: "reminder_same_day", status: live ? "approved" : "not_submitted", metaName: live ? "ot_reminder_same_day_v1" : null, submittedAt: live ? "2026-08-28T04:00:00Z" : null, reviewedAt: live ? "2026-08-28T09:14:00Z" : null, rejectionReason: null },
            { key: "stall_nudge", status: live ? "approved" : "not_submitted", metaName: live ? "ot_stall_nudge_v1" : null, submittedAt: live ? "2026-08-28T04:00:00Z" : null, reviewedAt: live ? "2026-08-28T09:14:00Z" : null, rejectionReason: null },
            { key: "post_visit_rebook", status: "not_submitted", metaName: null, submittedAt: null, reviewedAt: null, rejectionReason: null },
            { key: "no_show_recovery", status: live ? "pending" : "not_submitted", metaName: live ? "ot_no_show_recovery_v1" : null, submittedAt: live ? "2026-09-01T02:00:00Z" : null, reviewedAt: null, rejectionReason: null },
            { key: "morning_digest", status: live ? "approved" : "not_submitted", metaName: live ? "ot_morning_digest_v1" : null, submittedAt: live ? "2026-08-28T04:00:00Z" : null, reviewedAt: live ? "2026-08-28T09:15:00Z" : null, rejectionReason: null },
        ],
    };
    whatsapp.set(merchantId, state);
    return state;
}

// ─── Merchant config (#4667) ─────────────────────────────────────────────────

export function getMerchant(merchantId: string): Promise<MerchantResult> {
    const m = merchants.get(merchantId);
    return wait<MerchantResult>(m ? { ok: true, merchant: withLocks(m) } : { ok: false, kind: "notFound" });
}

export function saveMerchant(merchantId: string, config: MerchantConfigInput): Promise<SaveMerchantResult> {
    const current = merchants.get(merchantId);
    if (!current) return wait<SaveMerchantResult>({ ok: false, kind: "error", message: "Merchant not found." });
    // The server's rules, so the screens can be built against them: a locked
    // rule cannot be switched off, and an hours row is both set or both null.
    const locked = withLocks({ ...current, category: config.category });
    for (const r of config.escalationRules) {
        const rule = locked.escalationRules.find((x) => x.key === r.key);
        if (rule?.locked && !r.enabled) {
            return wait<SaveMerchantResult>({
                ok: false, kind: "invalid", code: "escalation_rule_locked",
                message: `${rule.label} cannot be switched off for a ${config.category}.`,
            });
        }
    }
    for (const h of config.hours) {
        if ((h.open === null) !== (h.close === null)) {
            return wait<SaveMerchantResult>({
                ok: false, kind: "invalid",
                message: "Opening hours need both an opening and a closing time, or neither.",
            });
        }
    }
    // Read-only fields are the server's; a client cannot set them. A service's
    // image is kept when the save leaves it out, as the server does (#6681).
    const images = new Map(current.services.map((s) => [s.id, { imageKey: s.imageKey ?? null, imageUrl: s.imageUrl ?? null }]));
    const next: MerchantConfig = withLocks({
        ...config,
        services: config.services.map((s) => ({ ...s, ...(images.get(s.id) ?? { imageKey: null, imageUrl: null }) })),
        id: current.id,
        whatsappConnected: current.whatsappConnected,
        approvedTemplates: current.approvedTemplates,
        staffChannel: { name: config.staffChannel.name, connected: current.staffChannel.connected, memberCount: current.staffChannel.memberCount },
    });
    merchants.set(merchantId, next);
    return wait<SaveMerchantResult>({ ok: true, merchant: next });
}

function setServiceImage(merchantId: string, serviceId: string, image: { imageKey: string | null; imageUrl: string | null }): Promise<ServiceImageResult> {
    const current = merchants.get(merchantId);
    if (!current || !current.services.some((s) => s.id === serviceId)) {
        return wait<ServiceImageResult>({ ok: false, message: "Save the service first, then add its image." });
    }
    const next = { ...current, services: current.services.map((s) => (s.id === serviceId ? { ...s, ...image } : s)) };
    merchants.set(merchantId, next);
    return wait<ServiceImageResult>({ ok: true, merchant: withLocks(next) });
}

export function uploadServiceImage(merchantId: string, serviceId: string, file: File): Promise<ServiceImageResult> {
    return setServiceImage(merchantId, serviceId, {
        imageKey: `receptionist/services/${merchantId}/${serviceId}_${Date.now()}`,
        imageUrl: URL.createObjectURL(file),
    });
}

export async function uploadServiceDetails(merchantId: string, serviceId: string, file: File): Promise<ServiceImageResult> {
    const current = merchants.get(merchantId);
    if (!current || !current.services.some((s) => s.id === serviceId)) {
        return wait<ServiceImageResult>({ ok: false, message: "Save the service first, then add its details." });
    }
    const text = file.name.toLowerCase().endsWith(".pdf") ? `(Text read from ${file.name})` : (await file.text()).trim().slice(0, 10_000);
    const next = { ...current, services: current.services.map((s) => (s.id === serviceId
        ? { ...s, details: text, detailsSource: { kind: "file" as const, fileName: file.name, updatedAt: new Date().toISOString() } }
        : s)) };
    merchants.set(merchantId, next);
    return wait<ServiceImageResult>({ ok: true, merchant: withLocks(next) });
}

export function deleteServiceImage(merchantId: string, serviceId: string): Promise<ServiceImageResult> {
    return setServiceImage(merchantId, serviceId, { imageKey: null, imageUrl: null });
}

// ─── Booking flow (#4668) ────────────────────────────────────────────────────

export function getFlow(merchantId: string): Promise<BookingFlow> {
    const existing = flows.get(merchantId);
    if (existing) return wait(existing);
    const m = merchants.get(merchantId);
    const flow = defaultBookingFlow();
    if (m) flow.opener.newCustomerOpen = flow.opener.newCustomerOpen.replace("Ownlab Secret Skin", m.name);
    flows.set(merchantId, flow);
    return wait(flow);
}

export function saveFlow(merchantId: string, input: BookingFlowInput): Promise<SaveFlowResult> {
    const current = flows.get(merchantId);
    // The registry's word on each template, never the client's: what the store
    // already held, or unsubmitted for a new or re-worded follow-up.
    const flow: BookingFlow = {
        ...input,
        followUps: input.followUps.map((f) => {
            const prev = current?.followUps.find((p) => p.id === f.id);
            return { ...f, templateStatus: prev && prev.message === f.message ? prev.templateStatus : "not_submitted" };
        }),
    };
    const problems = validateFlow(flow);
    if (problems.length) return wait<SaveFlowResult>({ ok: false, kind: "invalid", problems });
    const next: BookingFlow = { ...flow, version: (current?.version ?? flow.version) + 1 };
    flows.set(merchantId, next);
    return wait<SaveFlowResult>({ ok: true, flow: next });
}

export function getFlowTemplates(): Promise<FlowTemplate[]> {
    return wait<FlowTemplate[]>([
        { key: "ourteam_default", label: "Standard booking", description: "Ask what, ask when, ask who, then hold for your team.", flow: defaultBookingFlow() },
        { key: "ourteam_clinic", label: "Clinic with screening", description: "The standard flow with a contraindication screen that flags a booking for a human.", flow: clinicBookingFlow() },
    ]);
}

// ─── Bookings (#4669) ────────────────────────────────────────────────────────

const isClosed = (b: Booking) => b.status === "Declined" || b.status === "Cancelled";

/** The server's search, for the fixtures: a name contains it, or the phone's digits do. */
function matchesSearch(b: Booking, search: string | undefined): boolean {
    const term = (search ?? "").trim().toLowerCase();
    if (!term) return true;
    if (b.customer.toLowerCase().includes(term)) return true;
    const digits = term.replace(/\D/g, "");
    if (digits.length < 3) return false;
    const phone = b.phone.replace(/\D/g, "");
    return phone.includes(digits) || (digits.startsWith("60") && phone.includes("0" + digits.slice(2)));
}

export function listBookings(merchantId: string, params: ListBookingsParams = {}): Promise<BookingsPage> {
    const all = (bookings.get(merchantId) ?? []).filter((b) => matchesSearch(b, params.search));
    const status = params.status ?? "all";
    const filtered = all.filter((b) =>
        status === "all" ? true
        : status === "closed" ? isClosed(b)
        : status === "pending" ? b.status === "Pending"
        : b.status === "Confirmed");
    const limit = Math.min(params.limit ?? 50, 100);
    const page = Math.max(1, params.page ?? 1);
    const start = (page - 1) * limit;
    return wait<BookingsPage>({
        counts: {
            all: all.length,
            pending: all.filter((b) => b.status === "Pending").length,
            confirmed: all.filter((b) => b.status === "Confirmed").length,
            closed: all.filter(isClosed).length,
        },
        pagination: { total: filtered.length, page, limit, total_pages: Math.max(1, Math.ceil(filtered.length / limit)) },
        bookings: filtered.slice(start, start + limit),
    });
}

function decide(bookingId: string, confirmed: boolean): Promise<BookingDecisionResult> {
    for (const list of Array.from(bookings.values())) {
        const b = list.find((x: Booking) => x.id === bookingId);
        if (!b) continue;
        if (b.status !== "Pending") {
            return wait<BookingDecisionResult>({ ok: false, kind: "not_pending", message: "This booking is no longer pending." });
        }
        const outcome = confirmed
            ? `Good news, ${b.customer}: your ${b.service} on ${b.slot} is confirmed. See you then!`
            : `Sorry ${b.customer}, that slot is not available. Would another day or time work?`;
        b.status = confirmed ? "Confirmed" : "Declined";
        b.note = `${confirmed ? "Confirmed" : "Declined"} here just now`;
        b.transcript = [
            ...b.transcript,
            { who: "system", text: `${confirmed ? "Confirmed" : "Declined"} from the console` },
            { who: "ai", text: outcome },
        ];
        return wait<BookingDecisionResult>({ ok: true, booking: b });
    }
    return wait<BookingDecisionResult>({ ok: false, kind: "error", message: "Booking not found." });
}

export const confirmBooking = (bookingId: string): Promise<BookingDecisionResult> => decide(bookingId, true);
export const declineBooking = (bookingId: string): Promise<BookingDecisionResult> => decide(bookingId, false);

/**
 * The server's rule, so the composer is built against it: a free-form message
 * only goes out inside WhatsApp's 24-hour window. Outside it, a 409.
 */
export function sendBookingMessage(bookingId: string, input: BookingMessageInput): Promise<SendBookingMessageResult> {
    for (const list of Array.from(bookings.values())) {
        const b = list.find((x: Booking) => x.id === bookingId);
        if (!b) continue;
        // A template is the one thing that still delivers outside the window;
        // a free-form reply is refused, which is the server's rule.
        if ("text" in input && !isReplyWindowOpen(b)) {
            return wait<SendBookingMessageResult>({
                ok: false, kind: "window_closed",
                message: "WhatsApp only delivers replies within 24 hours of the customer's last message.",
            });
        }
        if ("template" in input) {
            const merchantId = Array.from(bookings.entries()).find(([, l]) => l === list)?.[0];
            const approved = merchantId
                ? (whatsapp.get(merchantId)?.templates ?? []).some((tpl) => tpl.key === input.template && tpl.status === "approved")
                : false;
            if (!approved) {
                return wait<SendBookingMessageResult>({
                    ok: false, kind: "error",
                    message: "That template is not approved by Meta yet.",
                });
            }
        }
        const text = "text" in input ? input.text : TEMPLATE_BODIES[input.template](b);
        b.transcript = [...b.transcript, { who: "ai", text }];
        return wait<SendBookingMessageResult>({ ok: true, booking: b });
    }
    return wait<SendBookingMessageResult>({ ok: false, kind: "error", message: "Booking not found." });
}

/** What each approved template actually says, so the thread reads truthfully. */
const TEMPLATE_BODIES: Record<WhatsappTemplateKey, (b: Booking) => string> = {
    booking_confirmed: (b) => `Good news, ${b.customer}: your ${b.service} on ${b.slot} is confirmed. See you then!`,
    reminder_24h: (b) => `Reminder, ${b.customer}: your ${b.service} is tomorrow, ${b.slot}. Reply here if you need to change it.`,
    reminder_same_day: (b) => `See you today, ${b.customer} — ${b.service} at ${b.slot}.`,
    stall_nudge: (b) => `Hi ${b.customer}, still keen on the ${b.service}? Reply here and we will hold ${b.slot} for you.`,
    post_visit_rebook: (b) => `Thanks for visiting, ${b.customer}. Would you like to book your next ${b.service}?`,
    no_show_recovery: (b) => `We missed you at ${b.slot}, ${b.customer}. Shall we find another time for your ${b.service}?`,
    morning_digest: (b) => `Overnight enquiry from ${b.customer}: ${b.service}, ${b.slot}.`,
};

// ─── Staff roll-up (#4670) ───────────────────────────────────────────────────

export function listMerchants(params: ListMerchantsParams = {}): Promise<MerchantsPage> {
    const rows: MerchantSummary[] = [
        { id: "ownlab-secret-skin", name: "Ownlab Secret Skin", category: "clinic", outlet: "Publika", whatsappConnected: true, onboardingStage: "kb_signoff", pendingBookings: 3, bookingsLast7Days: 12, escalationsLast7Days: 1, lastActivityAt: "2026-09-02T11:42:00Z" },
        { id: "urban-sprin", name: "Urban Sprin", category: "salon", outlet: "Bangsar", whatsappConnected: true, onboardingStage: "templates_pending", pendingBookings: 2, bookingsLast7Days: 4, escalationsLast7Days: 1, lastActivityAt: "2026-09-02T13:15:00Z" },
        { id: "serene-spa", name: "Serene Spa", category: "spa", outlet: "Mont Kiara", whatsappConnected: false, onboardingStage: "number_migration", pendingBookings: 0, bookingsLast7Days: 0, escalationsLast7Days: 0, lastActivityAt: null },
        { id: "pearl-dental", name: "Pearl Dental", category: "dental", outlet: "Damansara Heights", whatsappConnected: false, onboardingStage: "details", pendingBookings: 0, bookingsLast7Days: 0, escalationsLast7Days: 0, lastActivityAt: null },
    ];
    const q = (params.search ?? "").trim().toLowerCase();
    const filtered = q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
    const limit = Math.min(params.limit ?? 50, 100);
    const page = Math.max(1, params.page ?? 1);
    return wait<MerchantsPage>({
        pagination: { total: filtered.length, page, limit, total_pages: Math.max(1, Math.ceil(filtered.length / limit)) },
        merchants: filtered.slice((page - 1) * limit, page * limit),
    });
}

// ─── Staff channel (#4711) ───────────────────────────────────────────────────

/**
 * The demo dataset mirrors the server's rule rather than the old flag: a member
 * is connected once we have heard from them, so `memberCount` counts people we
 * could actually reach and `invitedCount` carries the `people[]` figure. The
 * first merchant has a live team, the rest are waiting on the link.
 */
/** The Overview from the sample queue: pending as the queue says, the rest a plausible week. */
export function getOverview(merchantId: string): Promise<ReceptionistOverview> {
    const all = bookings.get(merchantId) ?? [];
    const pending = all.filter((b) => b.status === "Pending");
    const now = Date.now();
    const today = new Date();
    const at = (h: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, 0).toISOString();
    return wait<ReceptionistOverview>({
        windowDays: 7,
        since: new Date(now - 7 * 864e5).toISOString(),
        pending: { count: pending.length, oldestAt: pending.length ? new Date(now - 26 * 36e5).toISOString() : null },
        bookings: { count: 12, previous: 9, confirmed: 7, declined: 1, afterHours: 4 },
        conversations: { count: 41, previous: 35, aiReplies: 188 },
        handedToTeam: { count: 3, previous: 5 },
        followUpsSent: 9,
        nudged: 2,
        today: [
            { id: "t1", customer: "Nurul Aina", service: "Hydra Glow Facial", slotAt: at(11) },
            { id: "t2", customer: "Mei Ling", service: "Chemical Peel", slotAt: at(15) },
        ],
    });
}

export function getStaffChannel(merchantId: string): Promise<StaffChannelState> {
    const m = merchants.get(merchantId);
    const people = m?.people ?? [];
    const live = m?.staffChannel.connected ?? false;
    const now = Date.now();
    const members = people.map((p, i) => {
        // Everyone on a connected team has answered; on the others, nobody has.
        const connected = live;
        // First member messaged 90 minutes ago (window open); the second 40 hours
        // ago, which is past the 24-hour window. The two must agree — a window
        // cannot be open on the older message and shut on the newer one.
        const minutesAgo = i === 0 ? 90 : 40 * 60;
        const lastSeen = connected ? new Date(now - minutesAgo * 60_000).toISOString() : null;
        return {
            id: p.id,
            name: p.name,
            role: p.role,
            phone: p.phone,
            connected,
            lastSeenAt: lastSeen,
            // Derived from the same timestamp rather than set by hand, so the
            // fixture can never claim a state the timestamp contradicts.
            windowOpen: connected && minutesAgo < 24 * 60,
        };
    });
    const reached = members.filter((x) => x.connected);
    return wait<StaffChannelState>({
        name: m?.staffChannel.name ?? "",
        connected: reached.length > 0,
        memberCount: reached.length,
        invitedCount: members.length,
        connectedAt: reached.length > 0 ? new Date(now - 26 * 3_600_000).toISOString() : null,
        connectLink: `https://wa.me/60123456789?text=${encodeURIComponent(`[RCP:${merchantId}] Connecting me to the ${m?.name ?? "team"} front desk`)}`,
        members,
    });
}

export function sendStaffChannelTest(merchantId: string): Promise<SendStaffTestResult> {
    const m = merchants.get(merchantId);
    const reached = m?.staffChannel.connected ? (m?.people ?? []) : [];
    if (reached.length === 0) {
        return wait<SendStaffTestResult>({
            ok: false,
            kind: "not_connected",
            message: "No one on your team has connected yet. Share the connect link and ask them to send the message.",
        });
    }
    return wait<SendStaffTestResult>({
        ok: true,
        delivered: true,
        sentTo: reached.map((p) => ({ phone: p.phone, status: "sent" })),
        memberCount: reached.length,
    });
}

// ─── WhatsApp onboarding (#4671) ─────────────────────────────────────────────

export const getWhatsappState = (merchantId: string): Promise<WhatsappState> => wait(whatsappFor(merchantId));

export function signOffKnowledgeBase(merchantId: string): Promise<WhatsappState> {
    const s = whatsappFor(merchantId);
    if (!s.checklist.kbSignedOff) {
        s.checklist.kbSignedOff = true;
        s.kbSignedOffAt = new Date().toISOString();
        s.kbSignedOffBy = "You";
    }
    return wait(s);
}

export function acceptWhatsappTerms(merchantId: string): Promise<void> {
    whatsappFor(merchantId).oneWayDoorAcceptedAt = new Date().toISOString();
    return wait(undefined);
}

export function startWhatsappMigration(merchantId: string): Promise<MigrateResult> {
    const s = whatsappFor(merchantId);
    if (!s.oneWayDoorAcceptedAt) {
        return wait<MigrateResult>({ ok: false, kind: "terms_not_accepted", message: "Accept that the number leaves the WhatsApp Business app before starting the move." });
    }
    s.checklist.metaVerified = true;
    s.onboardingStage = "number_migration";
    return wait<MigrateResult>({ ok: true, state: s });
}

export function submitTemplates(merchantId: string, keys?: string[]): Promise<WhatsappState> {
    const s = whatsappFor(merchantId);
    s.templates = s.templates.map((t) =>
        t.status === "not_submitted" && (!keys?.length || keys.includes(t.key))
            ? { ...t, status: "pending", metaName: `ot_${t.key}_v1`, submittedAt: new Date().toISOString() }
            : t);
    if (s.onboardingStage === "templates_pending" && s.templates.every((t) => t.status !== "not_submitted")) {
        // Meta's review is still pending; the stage moves on approval, not submission.
    }
    return wait(s);
}

// ─── Learn from chats (#4672) ────────────────────────────────────────────────

export function uploadTranscripts(merchantId: string, files: File[]): Promise<TranscriptFile[]> {
    const rows: TranscriptFile[] = files.map((f, i) => {
        const ok = /\.(txt|zip)$/i.test(f.name);
        return ok
            ? { id: `t_${Date.now()}_${i}`, name: f.name, messageCount: Math.max(4, Math.round(f.size / 90)), status: "parsed" }
            : { id: `t_${Date.now()}_${i}`, name: f.name, messageCount: 0, status: "failed", error: "Not a WhatsApp export — choose the .txt or .zip that WhatsApp's Export Chat produces." };
    });
    transcripts.set(merchantId, [...(transcripts.get(merchantId) ?? []), ...rows]);
    return wait(rows);
}

export function analyseTranscripts(merchantId: string, fileIds: string[]): Promise<AnalyseResult> {
    const known = (transcripts.get(merchantId) ?? []).filter((t) => fileIds.includes(t.id) && t.status === "parsed");
    return wait<AnalyseResult>({ status: "done", threadCount: Math.max(known.length, 12), proposals: demoProposals() });
}

export function getAnalysisJob(merchantId: string, jobId: string): Promise<AnalyseResult> {
    // The fixture never answers 202, so there is never a job to look up; the
    // parameter exists for signature parity with the live client.
    void jobId;
    return analyseTranscripts(merchantId, (transcripts.get(merchantId) ?? []).map((t) => t.id));
}

export async function applyProposals(merchantId: string, proposalIds: string[]): Promise<ApplyProposalsResult> {
    const m = merchants.get(merchantId);
    if (!m) return wait<ApplyProposalsResult>({ ok: false, kind: "error", message: "Merchant not found." });
    const accepted = demoProposals().filter((p) => proposalIds.includes(p.id));
    const flow = clone(flows.get(merchantId) ?? defaultBookingFlow());
    const next = clone(m);
    for (const p of accepted) {
        if (p.kind === "tone") next.tone = p.title === "Salon casual" ? "salon_casual" : "clinic_formal";
        if (p.kind === "faq") next.faqNotes = [...next.faqNotes, { label: p.title, body: p.detail }];
        if (p.kind === "policy") next.depositPolicy = p.quote ?? p.detail;
        if (p.kind === "service") next.services = [...next.services, { id: `svc_${next.services.length + 1}`, name: p.title.split(",")[0], durationMinutes: 60, price: 450, firstVisitPrice: 450, bookable: true }];
        if (p.kind === "step" && p.title.includes("first visit") && !flow.steps.some((s) => s.type === "screening")) {
            flow.steps.splice(flow.steps.length - 1, 0, { id: `s_${Math.random().toString(36).slice(2, 9)}`, type: "screening", advance: "auto", questions: [{ ref: "first_visit", prompt: "Is this your first visit with us?", options: ["Yes", "No"] }] });
        }
    }
    const problems = validateFlow(flow);
    if (problems.length) return wait<ApplyProposalsResult>({ ok: false, kind: "invalid", problems });
    merchants.set(merchantId, withLocks(next));
    flows.set(merchantId, flow);
    return wait<ApplyProposalsResult>({ ok: true, merchant: withLocks(next), flow });
}

export function deleteTranscripts(merchantId: string): Promise<void> {
    transcripts.delete(merchantId);
    return wait(undefined);
}
